import {
    ISO286_BASIC_SIZE_INCLUSIVE_UPPER_MM,
    toleranceTableColumnIndex
} from '../utils/metrologyToleranceColumn';
import {
    HOLE_FUNDAMENTAL_EI_MICRONS,
    SHAFT_FUNDAMENTAL_TAB_MICRONS,
    getIso286FundamentalColumnIndex
} from './iso286FundamentalTables';

/**
 * ISO 286 tolerance lookup - hole and shaft tolerances per ISO 286-1/2.
 * H1–H18 / h1–h18 IT zone widths (mm) and basic-size steps follow the metric charts at
 * https://www.machiningdoctor.com/calculators/tolerances/tol/ (same as ISO 286-2 presentation).
 * Nominal columns: left bound exclusive, right bound inclusive.
 */

export type Iso286ToleranceType = 'plusminus' | 'plus' | 'minus';

export interface Iso286LookupResult {
    tolerancePlus: string;
    toleranceMinus: string;
    toleranceType: Iso286ToleranceType;
}

/**
 * ISO 286-2 basic size column index (0..22). On each step boundary the nominal uses the
 * smaller-size column (inclusive upper limit of that band).
 */
function getSizeRangeIndex(nominalMm: number): number {
    return toleranceTableColumnIndex(
        nominalMm,
        ISO286_BASIC_SIZE_INCLUSIVE_UPPER_MM,
        'clamp-to-last-edge-column'
    );
}

/** Midpoint of the nominal band for column `idx` (ISO 286-2 / H-h 23-column grid). */
function nominalMidMmForIso286Column(idx: number): number {
    const U = ISO286_BASIC_SIZE_INCLUSIVE_UPPER_MM;
    const lo = idx <= 0 ? 0 : U[idx - 1];
    const hi = U[idx];
    return (lo + hi) / 2;
}

/**
 * Shaft rows on Machining Doctor: tabulated micron value is fundamental **ei** (lower deviation)
 * for transition / interference letters; for clearance letters it is **es** (upper deviation).
 */
const SHAFT_FUNDAMENTAL_USE_TAB_AS_EI_MICRONS = new Set([
    'k', 'm', 'n', 'p', 'r', 's', 't', 'u', 'v', 'x', 'y', 'z', 'za'
]);

function tryHoleIso286Fundamental(
    letters: string,
    grade: number,
    idx23: number,
    nominalMm: number
): Iso286LookupResult | null {
    const row = HOLE_FUNDAMENTAL_EI_MICRONS[letters];
    if (!row) {
        return null;
    }
    const i41 = getIso286FundamentalColumnIndex(nominalMm);
    const eiUm = row[i41];
    if (eiUm === null) {
        return null;
    }
    const itMm = itWidthHoleH(grade, idx23);
    if (itMm === null) {
        return null;
    }
    const eiMm = eiUm / 1000;
    const esMm = eiMm + itMm;
    if (eiMm >= -1e-12 && esMm >= eiMm - 1e-12) {
        return {
            tolerancePlus: fmtTolDev(esMm),
            toleranceMinus: fmtTolDev(eiMm < 0 ? 0 : eiMm),
            toleranceType: 'plus'
        };
    }
    if (eiMm < 0 && esMm > 1e-12) {
        return {
            tolerancePlus: fmtTolDev(esMm),
            toleranceMinus: fmtTolDev(Math.abs(eiMm)),
            toleranceType: 'plusminus'
        };
    }
    return {
        tolerancePlus: fmtTolDev(Math.abs(esMm)),
        toleranceMinus: fmtTolDev(Math.abs(eiMm)),
        toleranceType: 'minus'
    };
}

function tryShaftIso286Fundamental(
    letters: string,
    grade: number,
    idx23: number,
    nominalMm: number
): Iso286LookupResult | null {
    const row = SHAFT_FUNDAMENTAL_TAB_MICRONS[letters];
    if (!row) {
        return null;
    }
    const i41 = getIso286FundamentalColumnIndex(nominalMm);
    const fundUm = row[i41];
    if (fundUm === null) {
        return null;
    }
    const itMm = itWidthShaftH(grade, idx23);
    if (itMm === null) {
        return null;
    }
    if (SHAFT_FUNDAMENTAL_USE_TAB_AS_EI_MICRONS.has(letters)) {
        const eiMm = fundUm / 1000;
        const esMm = eiMm + itMm;
        return {
            tolerancePlus: fmtTolDev(esMm),
            toleranceMinus: fmtTolDev(Math.abs(eiMm)),
            toleranceType: 'plus'
        };
    }
    const esMm = fundUm / 1000;
    const eiMm = esMm - itMm;
    return {
        tolerancePlus: fmtTolDev(Math.abs(esMm)),
        toleranceMinus: fmtTolDev(Math.abs(eiMm)),
        toleranceType: 'minus'
    };
}

// Hole tolerances H1-H18 (upper positive, lower 0), mm — Machining Doctor metric H chart
const HOLE_H: Record<string, number[]> = {
    H1: [0.0008, 0.001, 0.001, 0.0012, 0.0015, 0.0015, 0.002, 0.0025, 0.0035, 0.0035, 0.0045, 0.0045, 0.006, 0.007, 0.008, 0.009, 0.01, 0.011, 0.013, 0.015, 0.018, 0.022, 0.026],
    H2: [0.0012, 0.0015, 0.0015, 0.002, 0.0025, 0.0025, 0.003, 0.004, 0.005, 0.005, 0.007, 0.007, 0.008, 0.009, 0.01, 0.011, 0.013, 0.015, 0.018, 0.021, 0.025, 0.03, 0.036],
    H3: [0.002, 0.0025, 0.0025, 0.003, 0.004, 0.004, 0.005, 0.006, 0.008, 0.008, 0.01, 0.01, 0.012, 0.013, 0.015, 0.016, 0.018, 0.021, 0.024, 0.029, 0.035, 0.041, 0.05],
    H4: [0.003, 0.004, 0.004, 0.005, 0.006, 0.007, 0.008, 0.01, 0.012, 0.012, 0.014, 0.014, 0.016, 0.018, 0.02, 0.022, 0.025, 0.028, 0.033, 0.039, 0.046, 0.055, 0.068],
    H5: [0.004, 0.005, 0.006, 0.008, 0.009, 0.011, 0.013, 0.015, 0.018, 0.018, 0.02, 0.02, 0.023, 0.025, 0.027, 0.032, 0.036, 0.04, 0.047, 0.055, 0.065, 0.078, 0.096],
    H6: [0.006, 0.008, 0.009, 0.011, 0.013, 0.016, 0.019, 0.022, 0.025, 0.025, 0.029, 0.029, 0.032, 0.036, 0.04, 0.044, 0.05, 0.056, 0.066, 0.078, 0.092, 0.11, 0.135],
    H7: [0.01, 0.012, 0.015, 0.018, 0.021, 0.025, 0.03, 0.035, 0.04, 0.04, 0.046, 0.046, 0.052, 0.057, 0.063, 0.07, 0.08, 0.09, 0.105, 0.125, 0.15, 0.175, 0.21],
    H8: [0.014, 0.018, 0.022, 0.027, 0.033, 0.039, 0.046, 0.054, 0.063, 0.063, 0.072, 0.072, 0.081, 0.089, 0.097, 0.11, 0.125, 0.14, 0.165, 0.195, 0.23, 0.28, 0.33],
    H9: [0.025, 0.03, 0.036, 0.043, 0.052, 0.062, 0.074, 0.087, 0.1, 0.1, 0.115, 0.115, 0.13, 0.14, 0.155, 0.175, 0.2, 0.23, 0.26, 0.31, 0.37, 0.44, 0.54],
    H10: [0.04, 0.048, 0.058, 0.07, 0.084, 0.1, 0.12, 0.14, 0.16, 0.16, 0.185, 0.185, 0.21, 0.23, 0.25, 0.28, 0.32, 0.36, 0.42, 0.5, 0.6, 0.7, 0.86],
    H11: [0.06, 0.075, 0.09, 0.11, 0.13, 0.16, 0.19, 0.22, 0.25, 0.25, 0.29, 0.29, 0.32, 0.36, 0.4, 0.44, 0.5, 0.56, 0.66, 0.78, 0.92, 1.1, 1.35],
    H12: [0.1, 0.12, 0.15, 0.18, 0.21, 0.25, 0.3, 0.35, 0.4, 0.4, 0.46, 0.46, 0.52, 0.57, 0.63, 0.7, 0.8, 0.9, 1.05, 1.25, 1.5, 1.75, 2.1],
    H13: [0.14, 0.18, 0.22, 0.27, 0.33, 0.39, 0.46, 0.54, 0.63, 0.63, 0.72, 0.72, 0.81, 0.89, 0.97, 1.1, 1.25, 1.4, 1.65, 1.95, 2.3, 2.8, 3.3],
    H14: [0.25, 0.3, 0.36, 0.43, 0.52, 0.62, 0.74, 0.87, 1, 1, 1.15, 1.15, 1.3, 1.4, 1.55, 1.75, 2, 2.3, 2.6, 3.1, 3.7, 4.4, 5.4],
    H15: [0.4, 0.48, 0.58, 0.7, 0.84, 1, 1.2, 1.4, 1.6, 1.6, 1.85, 1.85, 2.1, 2.3, 2.5, 2.8, 3.2, 3.6, 4.2, 5, 6, 7, 8.6],
    H16: [0.6, 0.75, 0.9, 1.1, 1.3, 1.6, 1.9, 2.2, 2.5, 2.5, 2.9, 2.9, 3.2, 3.6, 4, 4.4, 5, 5.6, 6.6, 7.8, 9.2, 11, 13.5],
    H17: [1, 1.2, 1.5, 1.8, 2.1, 2.5, 3, 3.5, 4, 4, 4.6, 4.6, 5.2, 5.7, 6.3, 7, 8, 9, 10.5, 12.5, 15, 17.5, 21],
    H18: [1.4, 1.8, 2.2, 2.7, 3.3, 3.9, 4.6, 5.4, 6.3, 6.3, 7.2, 7.2, 8.1, 8.9, 9.7, 11, 12.5, 14, 16.5, 19.5, 23, 28, 33]
};

// Shaft tolerances h1-h18 (upper 0, lower negative), mm — Machining Doctor metric h chart
const SHAFT_H: Record<string, number[]> = {
    h1: [0.0008, 0.001, 0.001, 0.0012, 0.0015, 0.0015, 0.002, 0.0025, 0.0035, 0.0035, 0.0045, 0.0045, 0.006, 0.007, 0.008, 0.009, 0.01, 0.011, 0.013, 0.015, 0.018, 0.022, 0.026],
    h2: [0.0012, 0.0015, 0.0015, 0.002, 0.0025, 0.0025, 0.003, 0.004, 0.005, 0.005, 0.007, 0.007, 0.008, 0.009, 0.01, 0.011, 0.013, 0.015, 0.018, 0.021, 0.025, 0.03, 0.036],
    h3: [0.002, 0.0025, 0.0025, 0.003, 0.004, 0.004, 0.005, 0.006, 0.008, 0.008, 0.01, 0.01, 0.012, 0.013, 0.015, 0.016, 0.018, 0.021, 0.024, 0.029, 0.035, 0.041, 0.05],
    h4: [0.003, 0.004, 0.004, 0.005, 0.006, 0.007, 0.008, 0.01, 0.012, 0.012, 0.014, 0.014, 0.016, 0.018, 0.02, 0.022, 0.025, 0.028, 0.033, 0.039, 0.046, 0.055, 0.068],
    h5: [0.004, 0.005, 0.006, 0.008, 0.009, 0.011, 0.013, 0.015, 0.018, 0.018, 0.02, 0.02, 0.023, 0.025, 0.027, 0.032, 0.036, 0.04, 0.047, 0.055, 0.065, 0.078, 0.096],
    h6: [0.006, 0.008, 0.009, 0.011, 0.013, 0.016, 0.019, 0.022, 0.025, 0.025, 0.029, 0.029, 0.032, 0.036, 0.04, 0.044, 0.05, 0.056, 0.066, 0.078, 0.092, 0.11, 0.135],
    h7: [0.01, 0.012, 0.015, 0.018, 0.021, 0.025, 0.03, 0.035, 0.04, 0.04, 0.046, 0.046, 0.052, 0.057, 0.063, 0.07, 0.08, 0.09, 0.105, 0.125, 0.15, 0.175, 0.21],
    h8: [0.014, 0.018, 0.022, 0.027, 0.033, 0.039, 0.046, 0.054, 0.063, 0.063, 0.072, 0.072, 0.081, 0.089, 0.097, 0.11, 0.125, 0.14, 0.165, 0.195, 0.23, 0.28, 0.33],
    h9: [0.025, 0.03, 0.036, 0.043, 0.052, 0.062, 0.074, 0.087, 0.1, 0.1, 0.115, 0.115, 0.13, 0.14, 0.155, 0.175, 0.2, 0.23, 0.26, 0.31, 0.37, 0.44, 0.54],
    h10: [0.04, 0.048, 0.058, 0.07, 0.084, 0.1, 0.12, 0.14, 0.16, 0.16, 0.185, 0.185, 0.21, 0.23, 0.25, 0.28, 0.32, 0.36, 0.42, 0.5, 0.6, 0.7, 0.86],
    h11: [0.06, 0.075, 0.09, 0.11, 0.13, 0.16, 0.19, 0.22, 0.25, 0.25, 0.29, 0.29, 0.32, 0.36, 0.4, 0.44, 0.5, 0.56, 0.66, 0.78, 0.92, 1.1, 1.35],
    h12: [0.1, 0.12, 0.15, 0.18, 0.21, 0.25, 0.3, 0.35, 0.4, 0.4, 0.46, 0.46, 0.52, 0.57, 0.63, 0.7, 0.8, 0.9, 1.05, 1.25, 1.5, 1.75, 2.1],
    h13: [0.14, 0.18, 0.22, 0.27, 0.33, 0.39, 0.46, 0.54, 0.63, 0.63, 0.72, 0.72, 0.81, 0.89, 0.97, 1.1, 1.25, 1.4, 1.65, 1.95, 2.3, 2.8, 3.3],
    h14: [0.25, 0.3, 0.36, 0.43, 0.52, 0.62, 0.74, 0.87, 1, 1, 1.15, 1.15, 1.3, 1.4, 1.55, 1.75, 2, 2.3, 2.6, 3.1, 3.7, 4.4, 5.4],
    h15: [0.4, 0.48, 0.58, 0.7, 0.84, 1, 1.2, 1.4, 1.6, 1.6, 1.85, 1.85, 2.1, 2.3, 2.5, 2.8, 3.2, 3.6, 4.2, 5, 6, 7, 8.6],
    h16: [0.6, 0.75, 0.9, 1.1, 1.3, 1.6, 1.9, 2.2, 2.5, 2.5, 2.9, 2.9, 3.2, 3.6, 4, 4.4, 5, 5.6, 6.6, 7.8, 9.2, 11, 13.5],
    h17: [1, 1.2, 1.5, 1.8, 2.1, 2.5, 3, 3.5, 4, 4, 4.6, 4.6, 5.2, 5.7, 6.3, 7, 8, 9, 10.5, 12.5, 15, 17.5, 21],
    h18: [1.4, 1.8, 2.2, 2.7, 3.3, 3.9, 4.6, 5.4, 6.3, 6.3, 7.2, 7.2, 8.1, 8.9, 9.7, 11, 12.5, 14, 16.5, 19.5, 23, 28, 33]
};

/** Pre-500 mm digitized pairs (old 14-column layout with 1 mm first step). */
const LEGACY_ISO286_PAIR_UPPER_MM = [1, 3, 6, 10, 18, 30, 50, 80, 120, 180, 250, 315, 400, 500] as const;

function legacyPairColumnIndex(nominalMidMm: number): number {
    return toleranceTableColumnIndex(nominalMidMm, LEGACY_ISO286_PAIR_UPPER_MM, 'clamp-to-last-edge-column');
}

/** G/F/E: TolDoctor page is H/h only; map midpoints to legacy pairs for d<=500, then scale by H IT vs column (400,500]. */
function expandHoleGfeFromLegacy(base14: readonly [number, number][], holeGrade: number): [number, number][] {
    const U = ISO286_BASIC_SIZE_INCLUSIVE_UPPER_MM;
    const hKey = ('H' + holeGrade) as keyof typeof HOLE_H;
    const hRow = HOLE_H[hKey];
    const out: [number, number][] = [];
    for (let i = 0; i < U.length; i++) {
        const loBound = i === 0 ? 0 : U[i - 1];
        const mid = (loBound + U[i]) / 2;
        if (mid <= 500) {
            const oi = legacyPairColumnIndex(mid);
            out.push([base14[oi][0], base14[oi][1]]);
        } else {
            const ref = out[13];
            const f = hRow[i] / hRow[13];
            out.push([ref[0] * f, ref[1] * f]);
        }
    }
    return out;
}

/** Shaft clearance / transition pairs: same mapping as holes using legacy columns and h IT scale past 500 mm. */
function expandShaftGfedFromLegacy(base14: readonly [number, number][], shaftGrade: number): [number, number][] {
    const U = ISO286_BASIC_SIZE_INCLUSIVE_UPPER_MM;
    const k = ('h' + shaftGrade) as keyof typeof SHAFT_H;
    const row = SHAFT_H[k];
    const out: [number, number][] = [];
    for (let i = 0; i < U.length; i++) {
        const loBound = i === 0 ? 0 : U[i - 1];
        const mid = (loBound + U[i]) / 2;
        if (mid <= 500) {
            const oi = legacyPairColumnIndex(mid);
            out.push([base14[oi][0], base14[oi][1]]);
        } else {
            const ref = out[13];
            const f = row[i] / row[13];
            out.push([ref[0] * f, ref[1] * f]);
        }
    }
    return out;
}

const HOLE_GFE_BASE: Record<string, [number, number][]> = {
    G6: [[0.008, 0.012], [0.009, 0.014], [0.011, 0.017], [0.013, 0.020], [0.016, 0.025], [0.019, 0.029], [0.022, 0.034], [0.025, 0.039], [0.029, 0.044], [0.032, 0.049], [0.036, 0.054], [0.040, 0.060], [0.046, 0.066], [0.052, 0.074]],
    G7: [[0.012, 0.016], [0.012, 0.020], [0.015, 0.024], [0.018, 0.028], [0.021, 0.034], [0.025, 0.040], [0.030, 0.047], [0.035, 0.054], [0.040, 0.061], [0.046, 0.069], [0.052, 0.075], [0.057, 0.083], [0.063, 0.092], [0.070, 0.102]],
    F7: [[0.010, 0.022], [0.013, 0.028], [0.016, 0.034], [0.020, 0.041], [0.025, 0.050], [0.030, 0.060], [0.036, 0.071], [0.043, 0.083], [0.050, 0.096], [0.056, 0.108], [0.062, 0.119], [0.068, 0.131], [0.074, 0.146], [0.083, 0.161]],
    F8: [[0.010, 0.028], [0.013, 0.035], [0.016, 0.043], [0.020, 0.053], [0.025, 0.064], [0.030, 0.076], [0.036, 0.090], [0.043, 0.106], [0.050, 0.122], [0.056, 0.137], [0.062, 0.151], [0.068, 0.165], [0.074, 0.180], [0.083, 0.196]],
    E8: [[0.020, 0.032], [0.025, 0.040], [0.032, 0.050], [0.040, 0.061], [0.050, 0.075], [0.060, 0.090], [0.072, 0.107], [0.085, 0.125], [0.100, 0.146], [0.110, 0.162], [0.125, 0.185], [0.140, 0.210], [0.155, 0.240], [0.175, 0.265]]
};

const HOLE_GFE: Record<string, [number, number][]> = {
    G6: expandHoleGfeFromLegacy(HOLE_GFE_BASE.G6, 6),
    G7: expandHoleGfeFromLegacy(HOLE_GFE_BASE.G7, 7),
    F7: expandHoleGfeFromLegacy(HOLE_GFE_BASE.F7, 7),
    F8: expandHoleGfeFromLegacy(HOLE_GFE_BASE.F8, 8),
    E8: expandHoleGfeFromLegacy(HOLE_GFE_BASE.E8, 8)
};

const SHAFT_GFED_BASE: Record<string, [number, number][]> = {
    g5: [[0.002, 0.006], [0.004, 0.008], [0.004, 0.009], [0.005, 0.010], [0.006, 0.012], [0.007, 0.014], [0.009, 0.016], [0.010, 0.018], [0.012, 0.021], [0.013, 0.023], [0.015, 0.026], [0.017, 0.029], [0.018, 0.032], [0.020, 0.036]],
    g6: [[0.002, 0.008], [0.004, 0.010], [0.005, 0.013], [0.006, 0.015], [0.007, 0.018], [0.009, 0.021], [0.010, 0.025], [0.012, 0.030], [0.014, 0.035], [0.015, 0.040], [0.017, 0.046], [0.018, 0.052], [0.020, 0.057], [0.022, 0.063]],
    g7: [[0.006, 0.016], [0.010, 0.020], [0.012, 0.024], [0.014, 0.028], [0.016, 0.034], [0.020, 0.040], [0.024, 0.047], [0.028, 0.054], [0.032, 0.061], [0.036, 0.069], [0.040, 0.075], [0.045, 0.083], [0.050, 0.092], [0.056, 0.102]],
    f6: [[0.006, 0.012], [0.010, 0.016], [0.013, 0.020], [0.016, 0.025], [0.020, 0.031], [0.025, 0.038], [0.030, 0.046], [0.036, 0.055], [0.043, 0.066], [0.050, 0.078], [0.056, 0.090], [0.062, 0.102], [0.068, 0.114], [0.074, 0.126]],
    f7: [[0.006, 0.016], [0.010, 0.020], [0.013, 0.025], [0.016, 0.031], [0.020, 0.038], [0.025, 0.046], [0.030, 0.055], [0.036, 0.066], [0.043, 0.078], [0.050, 0.090], [0.056, 0.102], [0.062, 0.114], [0.068, 0.125], [0.074, 0.137]],
    f8: [[0.010, 0.022], [0.013, 0.028], [0.016, 0.035], [0.020, 0.043], [0.025, 0.053], [0.030, 0.064], [0.036, 0.076], [0.043, 0.090], [0.050, 0.106], [0.056, 0.122], [0.062, 0.137], [0.068, 0.151], [0.074, 0.165], [0.083, 0.180]],
    e7: [[0.010, 0.020], [0.013, 0.025], [0.016, 0.032], [0.020, 0.040], [0.025, 0.050], [0.030, 0.061], [0.036, 0.075], [0.043, 0.090], [0.050, 0.107], [0.056, 0.125], [0.062, 0.146], [0.068, 0.168], [0.074, 0.192], [0.083, 0.217]],
    e8: [[0.020, 0.032], [0.025, 0.040], [0.032, 0.050], [0.040, 0.061], [0.050, 0.075], [0.060, 0.090], [0.072, 0.107], [0.085, 0.125], [0.100, 0.146], [0.110, 0.162], [0.125, 0.185], [0.140, 0.210], [0.155, 0.240], [0.175, 0.265]],
    d9: [[0.020, 0.045], [0.030, 0.060], [0.040, 0.076], [0.050, 0.093], [0.065, 0.117], [0.080, 0.142], [0.100, 0.174], [0.120, 0.207], [0.145, 0.245], [0.170, 0.285], [0.195, 0.330], [0.220, 0.375], [0.250, 0.425], [0.280, 0.480]],
    d10: [[0.040, 0.080], [0.048, 0.098], [0.058, 0.118], [0.070, 0.142], [0.084, 0.168], [0.100, 0.198], [0.120, 0.232], [0.140, 0.274], [0.168, 0.322], [0.195, 0.375], [0.228, 0.440], [0.260, 0.500], [0.300, 0.565], [0.340, 0.630]]
};

const SHAFT_GFED: Record<string, [number, number][]> = {
    g5: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.g5, 5),
    g6: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.g6, 6),
    g7: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.g7, 7),
    f6: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.f6, 6),
    f7: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.f7, 7),
    f8: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.f8, 8),
    e7: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.e7, 7),
    e8: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.e8, 8),
    d9: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.d9, 9),
    d10: expandShaftGfedFromLegacy(SHAFT_GFED_BASE.d10, 10)
};

const SHAFT_M_BASE: Record<string, [number, number][]> = {
    m6: [[0.002, 0.008], [0.004, 0.010], [0.006, 0.015], [0.007, 0.022], [0.008, 0.026], [0.009, 0.030], [0.011, 0.035], [0.013, 0.041], [0.015, 0.048], [0.018, 0.055], [0.020, 0.063], [0.022, 0.072], [0.025, 0.082], [0.028, 0.091]],
    k6: [[0, 0.006], [0, 0.008], [0.001, 0.010], [0.002, 0.012], [0.002, 0.015], [0.002, 0.018], [0.003, 0.021], [0.004, 0.025], [0.004, 0.030], [0.005, 0.035], [0.006, 0.040], [0.006, 0.046], [0.007, 0.052], [0.007, 0.057]]
};

const SHAFT_M: Record<string, [number, number][]> = {
    m6: expandShaftGfedFromLegacy(SHAFT_M_BASE.m6, 6),
    k6: expandShaftGfedFromLegacy(SHAFT_M_BASE.k6, 6)
};

const ISO286_DESIGNATION_REGEX = /(?:ISO\s*286\s*)?([A-Za-z][A-Za-z]?\s*\d{1,2})(?:\s*\/\s*([A-Za-z][A-Za-z]?\s*\d{1,2}))?(?:\s*ISO\s*286)?/i;

// Diameter symbols: Ø ø ⌀ ∅ (U+2205), o/O (OCR), φ/ϕ/Φ (OCR / PDF font substitution)
const DIAMETER_CHARS = /[Øø⌀∅oOφϕΦ]/g;

// Inch marker at end of string: straight quote ", prime ′/″, or typographic quotes ‘’
const INCH_MARK_RE = /["\u2032\u2033\u2019\u2018']\s*$/;
const INCH_TO_MM = 25.4;

export function parseNominalToMm(nominalStr: string): number | null {
    let s = nominalStr.replace(',', '.').replace(DIAMETER_CHARS, '').trim();
    // Strip editor/drawing prefix "C" for ISO 2768-1 Table 2 chamfer height callouts.
    if (/^[Cc](?=\s*(?:[\d.,]|[\dxXхХ×]))/i.test(s)) s = s.replace(/^\s*[Cc]\s*/, '');
    if (/^[Rr]/.test(s)) s = s.replace(/^[Rr]\s*/, '');
    // Detect inch notation: trailing inch mark OR fraction slash anywhere in the number part
    const isInch = INCH_MARK_RE.test(s) || /\d\s*\/\s*\d/.test(s);
    if (isInch) {
        const t = s.replace(INCH_MARK_RE, '').trim();
        const mixed = t.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
        if (mixed) {
            const w = parseInt(mixed[1], 10);
            const n = parseInt(mixed[2], 10);
            const d = parseInt(mixed[3], 10);
            if (d > 0) {
                const inches = w + n / d;
                return inches >= 0 ? inches * INCH_TO_MM : null;
            }
            return null;
        }
        const frac = t.match(/^(\d+)\s*\/\s*(\d+)$/);
        if (frac) {
            const n = parseInt(frac[1], 10);
            const d = parseInt(frac[2], 10);
            if (d > 0) {
                const inches = n / d;
                return inches >= 0 ? inches * INCH_TO_MM : null;
            }
            return null;
        }
        const decOnly = t.replace(/\s/g, '').match(/^(\d+(\.\d*)?|\.\d+)$/);
        if (decOnly) {
            let v = decOnly[0];
            if (v.startsWith('.')) v = '0' + v;
            const num = parseFloat(v);
            if (isNaN(num) || num < 0) return null;
            return num * INCH_TO_MM;
        }
        return null;
    }
    s = s.replace(/\s/g, '');
    const m = s.match(/^(\d+([.,]\d*)?|[.,]\d+)$/);
    if (!m) return null;
    let v = m[0];
    if (v.startsWith('.')) v = '0' + v;
    const num = parseFloat(v);
    return isNaN(num) || num < 0 ? null : num;
}

export function extractIso286Designation(tolerancePart: string): string | null {
    const m = tolerancePart.match(ISO286_DESIGNATION_REGEX);
    if (!m) return null;
    const first = m[1].replace(/\s/g, '');
    const second = m[2] ? m[2].replace(/\s/g, '') : null;
    if (second) {
        return `${first}/${second}`;
    }
    return first;
}

/** Standard ISO 286 tables only (no slash, no M-list extrapolation). */
function tryStandardIso286AtIndex(
    normDes: string,
    idx: number,
    nominalMm: number
): Iso286LookupResult | null {
    const holeMatch = normDes.match(/^H(\d{1,2})$/);
    if (holeMatch) {
        const key = 'H' + holeMatch[1];
        const arr = HOLE_H[key];
        if (arr && idx < arr.length) {
            return {
                tolerancePlus: arr[idx].toFixed(3).replace(/\.?0+$/, '') || '0',
                toleranceMinus: '0',
                toleranceType: 'plusminus'
            };
        }
    }

    const shaftHMatch = normDes.match(/^h(\d{1,2})$/);
    if (shaftHMatch) {
        const key = 'h' + shaftHMatch[1];
        const arr = SHAFT_H[key];
        if (arr && idx < arr.length) {
            return {
                tolerancePlus: '0',
                toleranceMinus: arr[idx].toFixed(3).replace(/\.?0+$/, '') || '0',
                toleranceType: 'plusminus'
            };
        }
    }

    const jsHoleMatch = normDes.match(/^JS(\d{1,2})$/);
    if (jsHoleMatch) {
        const hKey = ('H' + jsHoleMatch[1]) as keyof typeof HOLE_H;
        const arr = HOLE_H[hKey];
        if (arr && idx < arr.length) {
            const half = arr[idx] / 2;
            const fmt = (v: number) => v.toFixed(3).replace(/\.?0+$/, '') || '0';
            return {
                tolerancePlus: fmt(half),
                toleranceMinus: fmt(half),
                toleranceType: 'plusminus'
            };
        }
    }

    const jsShaftMatch = normDes.match(/^js(\d{1,2})$/);
    if (jsShaftMatch) {
        const hKey = ('H' + jsShaftMatch[1]) as keyof typeof HOLE_H;
        const arr = HOLE_H[hKey];
        if (arr && idx < arr.length) {
            const half = arr[idx] / 2;
            const fmt = (v: number) => v.toFixed(3).replace(/\.?0+$/, '') || '0';
            return {
                tolerancePlus: fmt(half),
                toleranceMinus: fmt(half),
                toleranceType: 'plusminus'
            };
        }
    }

    const holeFundMatch = normDes.match(/^([A-Z]{1,3})(\d{1,2})$/);
    if (holeFundMatch && normDes === normDes.toUpperCase()) {
        const letters = holeFundMatch[1];
        const grade = parseInt(holeFundMatch[2], 10);
        if (letters !== 'H' && letters !== 'JS') {
            const r = tryHoleIso286Fundamental(letters, grade, idx, nominalMm);
            if (r) {
                return r;
            }
        }
    }

    const shaftFundMatch = normDes.match(/^([a-z]+)(\d{1,2})$/);
    if (shaftFundMatch) {
        const letters = shaftFundMatch[1];
        const grade = parseInt(shaftFundMatch[2], 10);
        if (letters !== 'h' && letters !== 'js') {
            const r = tryShaftIso286Fundamental(letters, grade, idx, nominalMm);
            if (r) {
                return r;
            }
        }
    }

    return null;
}

export function lookupIso286Tolerance(
    designation: string,
    nominalMm: number
): Iso286LookupResult | null {
    const idx = getSizeRangeIndex(nominalMm);
    const normDes = designation.trim();

    const std = tryStandardIso286AtIndex(normDes, idx, nominalMm);
    if (std) return std;

    if (normDes.includes('/')) {
        const pair = parseIso286MSelectionPair(normDes);
        if (pair) {
            const holeRes = lookupIso286Tolerance(pair.hole, nominalMm);
            if (holeRes) return holeRes;
            return lookupIso286Tolerance(pair.shaft, nominalMm);
        }
    }

    return lookupIso286ToleranceFromMListData(normDes, idx);
}

export function isIso286RelatedText(text: string): boolean {
    return /ISO\s*286|^[A-Za-z][A-Za-z]?\d{1,2}(?:\s*\/\s*[A-Za-z][A-Za-z]?\d{1,2})?$/i.test(text.trim());
}

const ISO286_IN_SENTENCE_REGEX = /ISO\s*286(?:[-\s]*\d)?|EN\s*ISO\s*286/i;

export function hasIso286InSentences(sentences: string[]): boolean {
    return sentences.some(s => ISO286_IN_SENTENCE_REGEX.test(s.trim()));
}

export function hasDiameterMark(nominalStr: string): boolean {
    const t = (nominalStr || '').trim().normalize('NFC');
    return /^[Øø⌀∅oOφϕΦ]\s*\d/.test(t);
}

/** External radius per drawing (R before the numeric value). */
export function hasRadiusMark(nominalStr: string): boolean {
    const t = (nominalStr || '').trim().normalize('NFC');
    return /^[Rr]\s*\d/.test(t);
}

export const ISO286_DROPDOWN_OPTIONS = [
    'H7', 'H8', 'H9', 'H11',
    'h6', 'h7', 'h8', 'h9', 'h10', 'h11',
    'g6', 'f7', 'm6', 'k6',
    'H7/h6', 'H7/g6', 'H8/h9'
] as const;

export const ISO286_HOLE_OPTIONS = [
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'H7', 'H8', 'H9', 'H10', 'H11', 'H12', 'H13', 'H14', 'H15', 'H16', 'H17', 'H18',
    'G6', 'G7', 'F7', 'F8', 'E8'
] as const;

export const ISO286_SHAFT_OPTIONS = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'h7', 'h8', 'h9', 'h10', 'h11', 'h12', 'h13', 'h14', 'h15', 'h16', 'h17', 'h18',
    'g5', 'g6', 'g7', 'f6', 'f7', 'f8', 'e7', 'e8', 'd9', 'd10', 'm6', 'k6'
] as const;

/**
 * Manual tolerance list for M button dropdown (~268 options).
 * Format: uppercase/number / lowercase/number (e.g. H7/h7, G6/g6).
 * Applied designation depends on active button: hole -> uppercase part, shaft -> lowercase part.
 */
export const ISO286_M_FULL_TOLERANCES: { value: string; label: string }[] = (() => {
    const result: { value: string; label: string }[] = [
        { value: '', label: '—' }
    ];
    for (let g = 1; g <= 18; g++) {
        result.push({ value: `H/h${g}`, label: `H/h${g}` });
    }
    const holeLetters: string[] = [
        'G5', 'G6', 'G7', 'G8', 'G9', 'G10', 'G11', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11',
        'E5', 'E6', 'E7', 'E8', 'E9', 'E10', 'E11', 'E12', 'E13', 'D6', 'D7', 'D8', 'D9', 'D10', 'D11', 'D12',
        'C7', 'C8', 'C9', 'C10', 'C11', 'C12', 'B8', 'B9', 'B10', 'B11', 'B12', 'A9', 'A10', 'A11', 'A12', 'A13',
        'J5', 'J6', 'J7', 'J8', 'J9', 'JS5', 'JS6', 'JS7', 'JS8', 'JS9', 'JS10',
        'K5', 'K6', 'K7', 'K8', 'K9', 'M5', 'M6', 'M7', 'M8', 'M9', 'N5', 'N6', 'N7', 'N8', 'N9',
        'P5', 'P6', 'P7', 'P8', 'P9', 'R5', 'R6', 'R7', 'R8', 'R9', 'S5', 'S6', 'S7', 'S8', 'S9',
        'T5', 'T6', 'T7', 'T8', 'U6', 'U7', 'U8', 'V6', 'V7', 'V8', 'X6', 'X7', 'X8', 'Y6', 'Y7', 'Z6', 'Z7'
    ];
    holeLetters.forEach(opt => {
        const letter = opt.replace(/\d+$/, '');
        const num = opt.slice(letter.length);
        const lower = letter.toLowerCase() + num;
        result.push({ value: `${letter}/${lower}`, label: `${letter}/${lower}` });
    });
    return result;
})();

/**
 * Parse M-dropdown pair "H7/h7", "H/h18", "G6/g6", "JS6/js6" into hole and shaft designations for lookup and UI sync.
 */
export function parseIso286MSelectionPair(value: string): { hole: string; shaft: string } | null {
    const v = value.trim();
    if (!v || !v.includes('/')) return null;
    const parts = v.split('/').map(s => s.trim()).filter(Boolean);
    if (parts.length < 2) return null;
    const leftRaw = parts[0];
    const rightRaw = parts[1];
    if (!rightRaw) return null;

    if (!/\d/.test(leftRaw)) {
        const sm = rightRaw.match(/^([a-z]+)(\d{1,2})$/i);
        if (!sm) return null;
        const lowLetters = sm[1].toLowerCase();
        const num = sm[2];
        const holeLetters = leftRaw.replace(/[^A-Za-z]/g, '').toUpperCase();
        if (!holeLetters || !/^[A-Z]+$/.test(holeLetters)) return null;
        return { hole: holeLetters + num, shaft: lowLetters + num };
    }

    const hm = leftRaw.match(/^([A-Za-z]+)(\d{1,2})$/);
    const sm = rightRaw.match(/^([A-Za-z]+)(\d{1,2})$/);
    if (!hm || !sm) return null;
    const hole = hm[1].toUpperCase() + hm[2];
    const shaft = sm[1].toLowerCase() + sm[2];
    return { hole, shaft };
}

/**
 * True when the M-list pair uses a hole-side fundamental letter G…A (ISO internal / clearance
 * holes). Shaft side is the mating letter only; tolerance for the measurement is taken from the
 * hole designation — UI should not switch to "shaft" for these pairs.
 */
export function isIso286MPairHoleToleranceOnly(mValue: string | null): boolean {
    if (!mValue?.trim()) return false;
    const p = parseIso286MSelectionPair(mValue);
    if (!p) return false;
    if (/^JS\d/i.test(p.hole)) return false;
    return /^[GFEDCBA]/.test(p.hole);
}

export function parseHoleFromDesignation(designation: string | null): string | null {
    if (!designation || !designation.trim()) return null;
    const norm = designation.trim();
    if (/^[HGFE]\d{1,2}$/.test(norm)) return norm;
    const parts = norm.split('/').map(p => p.trim());
    const hole = parts.find(p => /^[HGFE]\d{1,2}$/.test(p));
    return hole || null;
}

export function parseShaftFromDesignation(designation: string | null): string | null {
    if (!designation || !designation.trim()) return null;
    const norm = designation.trim();
    if (/^[hgfedmk]\d{1,2}$/.test(norm)) return norm;
    const parts = norm.split('/').map(p => p.trim());
    const shaft = parts.find(p => /^[hgfedmk]\d{1,2}$/.test(p));
    return shaft || null;
}

// --- M-button list: embedded tolerance rows (23 nominal ranges) for every designation in ISO286_M_FULL_TOLERANCES ---

function fmtTolDev(v: number): string {
    return v.toFixed(3).replace(/\.?0+$/, '') || '0';
}

function itWidthHoleH(grade: number, idx: number): number | null {
    const k = ('H' + grade) as keyof typeof HOLE_H;
    const a = HOLE_H[k];
    if (!a || idx >= a.length) return null;
    return a[idx];
}

function itWidthShaftH(grade: number, idx: number): number | null {
    const k = ('h' + grade) as keyof typeof SHAFT_H;
    const a = SHAFT_H[k];
    if (!a || idx >= a.length) return null;
    return a[idx];
}

/** EI as fraction of IT for transition / interference holes (embedded policy data for M list). */
const HOLE_TRANSITION_EI_FRAC: Record<string, number> = {
    K: 0,
    M: 0.04,
    N: 0.08,
    P: 0.12,
    R: 0.16,
    S: 0.2,
    T: 0.24,
    U: 0.28,
    V: 0.32,
    X: 0.36,
    Y: 0.4,
    Z: 0.44
};

const SHAFT_INTERFERENCE_LETTER_BOOST: Record<string, number> = {
    n: 1,
    p: 1.1,
    r: 1.22,
    s: 1.35,
    t: 1.48,
    u: 1.62,
    v: 1.75,
    x: 1.88,
    y: 2,
    z: 2.1
};

function extrapolateHoleGFE(letter: 'G' | 'F' | 'E', grade: number, idx: number): Iso286LookupResult | null {
    const refKey = (letter === 'G'
        ? (grade <= 6 ? 'G6' : 'G7')
        : letter === 'F'
            ? (grade <= 7 ? 'F7' : 'F8')
            : 'E8') as keyof typeof HOLE_GFE;
    const ref = HOLE_GFE[refKey];
    if (!ref || idx >= ref.length) return null;
    const refGrade = parseInt(String(refKey).replace(/^\D+/, ''), 10);
    const itG = itWidthHoleH(grade, idx);
    const itRef = itWidthHoleH(refGrade, idx);
    if (itG === null || itRef === null || itRef === 0) return null;
    const scale = itG / itRef;
    const [lo, hi] = ref[idx];
    return {
        tolerancePlus: fmtTolDev(hi * scale),
        toleranceMinus: fmtTolDev(lo * scale),
        toleranceType: 'plus'
    };
}

function extrapolateHoleDCBA(first: string, grade: number, idx: number): Iso286LookupResult | null {
    const e8 = HOLE_GFE.E8[idx];
    if (!e8) return null;
    const it = itWidthHoleH(grade, idx);
    if (it === null) return null;
    const depthMap: Record<string, number> = { D: 1.15, C: 1.35, B: 1.55, A: 1.75 };
    const depth = depthMap[first] ?? 1.2;
    const it8 = itWidthHoleH(8, idx);
    if (it8 === null || it8 === 0) return null;
    const scale = it / it8;
    const [elo, ehi] = e8;
    return {
        tolerancePlus: fmtTolDev(ehi * scale * depth),
        toleranceMinus: fmtTolDev(elo * scale * depth),
        toleranceType: 'plus'
    };
}

function extrapolateHoleJ(grade: number, idx: number): Iso286LookupResult | null {
    const it = itWidthHoleH(grade, idx);
    if (it === null) return null;
    const half = it / 2;
    return {
        tolerancePlus: fmtTolDev(half),
        toleranceMinus: fmtTolDev(half),
        toleranceType: 'plusminus'
    };
}

function extrapolateHoleTransition(letters: string, grade: number, idx: number): Iso286LookupResult | null {
    const ch = letters[0];
    const z = HOLE_TRANSITION_EI_FRAC[ch];
    if (z === undefined) return null;
    const it = itWidthHoleH(grade, idx);
    if (it === null) return null;
    const ei = z * it;
    const es = ei + it;
    return {
        tolerancePlus: fmtTolDev(es),
        toleranceMinus: fmtTolDev(ei),
        toleranceType: 'plus'
    };
}

function extrapolateShaftGFEDLetter(L: string, grade: number, idx: number): Iso286LookupResult | null {
    const candidates = Object.keys(SHAFT_GFED)
        .filter(k => k.startsWith(L))
        .map(k => ({ k, g: parseInt(k.slice(1), 10) }))
        .sort((a, b) => a.g - b.g);
    if (candidates.length === 0) return null;
    let nearest = candidates[0];
    let bestDiff = Math.abs(nearest.g - grade);
    for (const c of candidates) {
        const d = Math.abs(c.g - grade);
        if (d < bestDiff) {
            bestDiff = d;
            nearest = c;
        }
    }
    const ref = SHAFT_GFED[nearest.k as keyof typeof SHAFT_GFED];
    if (!ref || idx >= ref.length) return null;
    const itRef = itWidthShaftH(nearest.g, idx);
    const itG = itWidthShaftH(grade, idx);
    if (itRef === null || itG === null || itRef === 0) return null;
    const scale = itG / itRef;
    const [up, lo] = ref[idx];
    return {
        tolerancePlus: fmtTolDev(up * scale),
        toleranceMinus: fmtTolDev(lo * scale),
        toleranceType: 'minus'
    };
}

function extrapolateShaftD(grade: number, idx: number): Iso286LookupResult | null {
    const d9 = SHAFT_GFED.d9;
    if (!d9 || idx >= d9.length) return null;
    const it9 = itWidthShaftH(9, idx);
    const itG = itWidthShaftH(grade, idx);
    if (it9 === null || itG === null || it9 === 0) return null;
    const scale = itG / it9;
    const [up, lo] = d9[idx];
    return {
        tolerancePlus: fmtTolDev(up * scale),
        toleranceMinus: fmtTolDev(lo * scale),
        toleranceType: 'minus'
    };
}

function extrapolateShaftJ(grade: number, idx: number): Iso286LookupResult | null {
    const it = itWidthShaftH(grade, idx);
    if (it === null) return null;
    const q = it / 4;
    return {
        tolerancePlus: fmtTolDev(q),
        toleranceMinus: fmtTolDev(q),
        toleranceType: 'plusminus'
    };
}

function extrapolateShaftClearanceScaledFromG6(mult: number, grade: number, idx: number): Iso286LookupResult | null {
    const g6 = SHAFT_GFED.g6;
    if (!g6 || idx >= g6.length) return null;
    const itG = itWidthShaftH(grade, idx);
    const it6 = itWidthShaftH(6, idx);
    if (itG === null || it6 === null || it6 === 0) return null;
    const scale = (itG / it6) * mult;
    const [up, lo] = g6[idx];
    return {
        tolerancePlus: fmtTolDev(up * scale),
        toleranceMinus: fmtTolDev(lo * scale),
        toleranceType: 'minus'
    };
}

function extrapolateShaftFromM6Like(letters: string, grade: number, idx: number, boost: number): Iso286LookupResult | null {
    const m6 = SHAFT_M.m6;
    if (!m6 || idx >= m6.length) return null;
    const itM6 = itWidthShaftH(6, idx);
    const itG = itWidthShaftH(grade, idx);
    if (itM6 === null || itG === null || itM6 === 0) return null;
    const scale = (itG / itM6) * boost;
    const [lo, up] = m6[idx];
    return {
        tolerancePlus: fmtTolDev(up * scale),
        toleranceMinus: fmtTolDev(lo * scale),
        toleranceType: 'plus'
    };
}

function extrapolateShaftMK(letters: string, grade: number, idx: number): Iso286LookupResult | null {
    const baseKey = (letters === 'm' ? 'm6' : 'k6') as keyof typeof SHAFT_M;
    const base = SHAFT_M[baseKey];
    if (!base || idx >= base.length) return null;
    const itRef = itWidthShaftH(6, idx);
    const itG = itWidthShaftH(grade, idx);
    if (itRef === null || itG === null || itRef === 0) return null;
    const scale = itG / itRef;
    const [lo, up] = base[idx];
    return {
        tolerancePlus: fmtTolDev(up * scale),
        toleranceMinus: fmtTolDev(lo * scale),
        toleranceType: 'plus'
    };
}

/**
 * Tolerance row for M-list designations not covered by digitized ISO 286-2 subsets above.
 * Uses embedded scaling rules from the same IT (H/h) bands as the rest of this module.
 */
function extrapolateMDesignation(designation: string, idx: number): Iso286LookupResult | null {
    const holeM = designation.match(/^([A-Z]{1,3})(\d{1,2})$/);
    if (holeM && designation === designation.toUpperCase()) {
        const letters = holeM[1];
        const grade = parseInt(holeM[2], 10);
        if (letters === 'H' || letters === 'JS') return null;
        if (letters[0] === 'J' && letters.length === 1) {
            return extrapolateHoleJ(grade, idx);
        }
        if ('GFE'.includes(letters[0]) && letters.length === 1) {
            return extrapolateHoleGFE(letters[0] as 'G' | 'F' | 'E', grade, idx);
        }
        if ('DCBA'.includes(letters[0])) {
            return extrapolateHoleDCBA(letters[0], grade, idx);
        }
        return extrapolateHoleTransition(letters, grade, idx);
    }

    const shaftM = designation.match(/^([a-z]+)(\d{1,2})$/);
    if (shaftM) {
        const letters = shaftM[1];
        const grade = parseInt(shaftM[2], 10);
        if (letters === 'h' || letters === 'js') return null;
        if (letters === 'm' || letters === 'k') {
            return extrapolateShaftMK(letters, grade, idx);
        }
        if ('gfed'.includes(letters[0]) && letters.length === 1) {
            return extrapolateShaftGFEDLetter(letters[0], grade, idx);
        }
        if (letters[0] === 'd') {
            return extrapolateShaftD(grade, idx);
        }
        if (letters[0] === 'j' && letters !== 'js') {
            return extrapolateShaftJ(grade, idx);
        }
        if (letters[0] === 'c') {
            return extrapolateShaftClearanceScaledFromG6(1.35, grade, idx);
        }
        if (letters[0] === 'b') {
            return extrapolateShaftClearanceScaledFromG6(1.2, grade, idx);
        }
        if (letters[0] === 'a') {
            return extrapolateShaftClearanceScaledFromG6(1.55, grade, idx);
        }
        const boost = SHAFT_INTERFERENCE_LETTER_BOOST[letters[0]];
        if (boost !== undefined) {
            return extrapolateShaftFromM6Like(letters, grade, idx, boost);
        }
    }

    return null;
}

const M_LIST_TOLERANCE_ROWS: Record<string, Iso286LookupResult[]> = {};
/** Bump when nominal range logic changes to drop stale M-list rows. */
const M_LIST_TOLERANCE_ROWS_CACHE_VERSION = 5;
let mListToleranceRowsCacheVersionApplied = 0;
let mListDesignationSet: Set<string> | null = null;

function getMListDesignationSet(): Set<string> {
    if (mListDesignationSet) return mListDesignationSet;
    const s = new Set<string>();
    for (const o of ISO286_M_FULL_TOLERANCES) {
        if (!o.value) continue;
        const p = parseIso286MSelectionPair(o.value);
        if (p) {
            s.add(p.hole);
            s.add(p.shaft);
        }
    }
    mListDesignationSet = s;
    return s;
}

function computeMListDesignationRow(designation: string, idx: number): Iso286LookupResult {
    const std = tryStandardIso286AtIndex(designation, idx, nominalMidMmForIso286Column(idx));
    if (std) return std;
    const ex = extrapolateMDesignation(designation, idx);
    if (ex) return ex;
    const tail = designation.match(/(\d{1,2})$/);
    if (tail) {
        const g = parseInt(tail[1], 10);
        const itH = itWidthHoleH(g, idx) ?? itWidthShaftH(g, idx);
        if (itH !== null) {
            return {
                tolerancePlus: fmtTolDev(itH),
                toleranceMinus: fmtTolDev(itH),
                toleranceType: 'plusminus'
            };
        }
    }
    return { tolerancePlus: '0', toleranceMinus: '0', toleranceType: 'plusminus' };
}

function ensureMListToleranceRows(designation: string): void {
    if (mListToleranceRowsCacheVersionApplied !== M_LIST_TOLERANCE_ROWS_CACHE_VERSION) {
        for (const k of Object.keys(M_LIST_TOLERANCE_ROWS)) {
            delete M_LIST_TOLERANCE_ROWS[k];
        }
        mListToleranceRowsCacheVersionApplied = M_LIST_TOLERANCE_ROWS_CACHE_VERSION;
    }
    if (M_LIST_TOLERANCE_ROWS[designation]) return;
    const row: Iso286LookupResult[] = [];
    const n = HOLE_H.H6.length;
    for (let i = 0; i < n; i++) {
        row.push(computeMListDesignationRow(designation, i));
    }
    M_LIST_TOLERANCE_ROWS[designation] = row;
}

function lookupIso286ToleranceFromMListData(des: string, idx: number): Iso286LookupResult | null {
    if (!getMListDesignationSet().has(des)) return null;
    ensureMListToleranceRows(des);
    return M_LIST_TOLERANCE_ROWS[des][idx];
}
